import React, { useState, useEffect } from 'react';
import { Card, Container, Row, Col, Alert, Button, Spinner, ProgressBar } from '@themesberg/react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFilePdf, faDownload, faSync, faSpinner } from '@fortawesome/free-solid-svg-icons';
import { useSelector } from 'react-redux';
import axiosInstance from '../../api/api';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import ProductivosLogo from '../../assets/img/productivos-logo.png';

const ReportePdfPage = () => {
    const [pdfUrl, setPdfUrl] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [progress, setProgress] = useState(0);
    const [statusMsg, setStatusMsg] = useState('');

    const ciemId = useSelector(state => state.ciem.ciemId);

    // Cleanup blob URL on unmount
    useEffect(() => {
        return () => { if (pdfUrl) URL.revokeObjectURL(pdfUrl); };
    }, [pdfUrl]);

    const safeFetch = async (url) => {
        try {
            const r = await axiosInstance.get(url);
            return r.data.data || r.data;
        } catch { return null; }
    };

    const stripHtml = (html) => {
        if (!html) return '';
        const tmp = document.createElement('div');
        tmp.innerHTML = html;
        return tmp.textContent || tmp.innerText || '';
    };

    const generatePdf = async () => {
        if (!ciemId) return;
        setLoading(true);
        setError(null);
        setProgress(0);
        if (pdfUrl) { URL.revokeObjectURL(pdfUrl); setPdfUrl(null); }

        try {
            // --- Fetch all data ---
            setStatusMsg('Cargando datos del CIEM...');
            setProgress(5);
            const ciemData = await safeFetch(`/ciem/${ciemId}`);
            if (!ciemData) throw new Error('No se pudo cargar la información del CIEM');

            setStatusMsg('Cargando equipo emprendedor...');
            setProgress(15);
            const teamData = await safeFetch(`/team/${ciemId}`);

            setStatusMsg('Cargando margen de contribución...');
            setProgress(25);
            const contributionData = await safeFetch(`/contribution/${ciemId}`);

            setStatusMsg('Cargando competidores...');
            setProgress(35);
            const competitorData = await safeFetch(`/demanda/competitor/${ciemId}`);

            setStatusMsg('Cargando financiación...');
            setProgress(45);
            const financiacionData = await safeFetch(`/condicionFinanciacion/${ciemId}`);

            setStatusMsg('Cargando inversores...');
            setProgress(55);
            const inversoresData = await safeFetch(`/potentialInvestor/${ciemId}`);

            setStatusMsg('Cargando gastos...');
            setProgress(65);
            const gastosData = await safeFetch(`/nonRecoverableInvestment/${ciemId}`);

            setStatusMsg('Cargando plan de negocio...');
            setProgress(75);
            const planData = await safeFetch(`/businessPlanBase/${ciemId}`);

            setStatusMsg('Cargando estado de resultados...');
            setProgress(85);
            const estadoData = await safeFetch(`/escenarioVenta/${ciemId}`);

            // --- Build PDF ---
            setStatusMsg('Generando PDF...');
            setProgress(90);

            const doc = new jsPDF('p', 'mm', 'letter');
            const pageW = doc.internal.pageSize.getWidth();
            const pageH = doc.internal.pageSize.getHeight();
            const margin = 20;
            const contentW = pageW - margin * 2;

            const primaryColor = [9, 72, 179];
            const darkColor = [33, 37, 41];
            const lightBg = [245, 247, 250];

            // Helper: add section title
            const addSectionTitle = (title, yPos) => {
                if (yPos > pageH - 40) { doc.addPage(); yPos = margin; }
                doc.setFillColor(...primaryColor);
                doc.roundedRect(margin, yPos, contentW, 10, 2, 2, 'F');
                doc.setFont('helvetica', 'bold');
                doc.setFontSize(13);
                doc.setTextColor(255, 255, 255);
                doc.text(title, margin + 5, yPos + 7);
                doc.setTextColor(...darkColor);
                return yPos + 15;
            };

            // Helper: add text block
            const addTextBlock = (label, text, yPos) => {
                if (yPos > pageH - 30) { doc.addPage(); yPos = margin; }
                doc.setFont('helvetica', 'bold');
                doc.setFontSize(10);
                doc.text(label + ':', margin, yPos);
                doc.setFont('helvetica', 'normal');
                const cleaned = stripHtml(text || 'No disponible');
                const lines = doc.splitTextToSize(cleaned, contentW - 5);
                doc.text(lines, margin, yPos + 5);
                return yPos + 5 + lines.length * 4.5;
            };

            // Helper: add footer
            const addFooter = () => {
                const pages = doc.internal.getNumberOfPages();
                for (let i = 2; i <= pages; i++) {
                    doc.setPage(i);
                    doc.setFontSize(8);
                    doc.setTextColor(150, 150, 150);
                    doc.text(`Página ${i - 1} de ${pages - 1}`, pageW / 2, pageH - 10, { align: 'center' });
                    doc.text('Productivos.org - Reporte CIEM', margin, pageH - 10);
                    doc.text(new Date().toLocaleDateString('es-CO'), pageW - margin, pageH - 10, { align: 'right' });
                }
            };

            // ============ PAGE 1: COVER ============
            // Background gradient effect
            doc.setFillColor(9, 72, 179);
            doc.rect(0, 0, pageW, pageH, 'F');
            doc.setFillColor(7, 55, 140);
            doc.rect(0, 0, pageW, pageH / 3, 'F');

            // Logo
            try {
                const img = new Image();
                img.src = ProductivosLogo;
                await new Promise((res, rej) => { img.onload = res; img.onerror = rej; });
                const logoW = 80;
                const logoH = (img.height / img.width) * logoW;
                doc.addImage(img, 'PNG', (pageW - logoW) / 2, 40, logoW, logoH);
            } catch { /* skip logo if fails */ }

            // Decorative line
            doc.setDrawColor(255, 255, 255);
            doc.setLineWidth(0.5);
            doc.line(margin + 20, 95, pageW - margin - 20, 95);

            // Title
            doc.setTextColor(255, 255, 255);
            doc.setFont('helvetica', 'bold');
            doc.setFontSize(28);
            const ciemName = ciemData.name || 'Idea de Negocio';
            const titleLines = doc.splitTextToSize(ciemName, contentW - 20);
            doc.text(titleLines, pageW / 2, 120, { align: 'center' });

            // Subtitle
            doc.setFontSize(14);
            doc.setFont('helvetica', 'normal');
            doc.text('Reporte del Modelo de Negocio', pageW / 2, 120 + titleLines.length * 12 + 10, { align: 'center' });

            // Info box
            const infoY = 180;
            doc.setFillColor(255, 255, 255, 30);
            doc.roundedRect(margin + 30, infoY, contentW - 60, 40, 3, 3, 'F');
            doc.setTextColor(220, 230, 255);
            doc.setFontSize(11);
            doc.text(`Código: ${ciemData.code || 'N/A'}`, pageW / 2, infoY + 12, { align: 'center' });
            doc.text(`Fecha de generación: ${new Date().toLocaleDateString('es-CO')}`, pageW / 2, infoY + 22, { align: 'center' });
            doc.text(`Productivos.org`, pageW / 2, infoY + 32, { align: 'center' });

            // Footer on cover
            doc.setFontSize(9);
            doc.setTextColor(180, 200, 255);
            doc.text('Este documento fue generado automáticamente por el sistema Productivos.org', pageW / 2, pageH - 20, { align: 'center' });

            // ============ PAGE 2+: CONTENT ============
            doc.addPage();
            let y = margin;

            // --- Table of Contents ---
            y = addSectionTitle('Contenido del Reporte', y);
            doc.setFont('helvetica', 'normal');
            doc.setFontSize(10);
            const toc = [
                '1. Información General del CIEM',
                '2. Equipo Emprendedor',
                '3. El Producto',
                '4. Margen de Contribución',
                '5. Clientes / Competidores',
                '6. Financiación',
                '7. Inversores Potenciales',
                '8. Gastos No Recuperables',
                '9. Plan de Negocio',
                '10. Estado de Resultados',
                '11. Sector Externo',
                '12. Estructura de Producción'
            ];
            toc.forEach(item => {
                doc.text('• ' + item, margin + 5, y);
                y += 6;
            });

            // --- 1. Info General ---
            doc.addPage();
            y = margin;
            y = addSectionTitle('1. Información General del CIEM', y);
            doc.autoTable({
                startY: y,
                margin: { left: margin, right: margin },
                head: [['Campo', 'Valor']],
                body: [
                    ['Nombre', ciemData.name || '-'],
                    ['Código', ciemData.code || '-'],
                    ['Descripción Empresa', stripHtml(ciemData.descriptionCompany) || '-'],
                    ['Descripción Producto', stripHtml(ciemData.descriptionProduct) || '-'],
                ],
                styles: { fontSize: 9, cellPadding: 4 },
                headStyles: { fillColor: primaryColor, textColor: [255, 255, 255] },
                alternateRowStyles: { fillColor: lightBg },
                columnStyles: { 0: { fontStyle: 'bold', cellWidth: 50 } }
            });
            y = doc.lastAutoTable.finalY + 10;

            // --- 2. Equipo Emprendedor ---
            doc.addPage();
            y = margin;
            y = addSectionTitle('2. Equipo Emprendedor', y);
            if (Array.isArray(teamData) && teamData.length > 0) {
                doc.autoTable({
                    startY: y,
                    margin: { left: margin, right: margin },
                    head: [['#', 'Nombre', 'Cédula', 'Profesión', 'Función', 'Email', 'Teléfono']],
                    body: teamData.map((m, i) => [
                        i + 1, m.nombreCompleto || '-', m.cedula || '-',
                        m.profesion || '-', m.funcion || '-', m.email || '-', m.telefono || '-'
                    ]),
                    styles: { fontSize: 8, cellPadding: 3 },
                    headStyles: { fillColor: primaryColor },
                    alternateRowStyles: { fillColor: lightBg }
                });
            } else {
                doc.setFontSize(10);
                doc.text('No hay miembros del equipo registrados.', margin, y);
            }

            // --- 3. El Producto ---
            doc.addPage();
            y = margin;
            y = addSectionTitle('3. El Producto', y);
            y = addTextBlock('Descripción del Producto', ciemData.descriptionProduct, y);

            // --- 4. Margen de Contribución ---
            doc.addPage();
            y = margin;
            y = addSectionTitle('4. Margen de Contribución', y);
            if (contributionData && contributionData.detailContributionDTOList?.length > 0) {
                doc.autoTable({
                    startY: y,
                    margin: { left: margin, right: margin },
                    head: [['Producto', 'PVU', 'CVU', 'MCU', '%MCU']],
                    body: contributionData.detailContributionDTOList.map(item => [
                        item.producto || '-',
                        item.pvupp ?? '-', item.cvupp ?? '-', item.mcupp ?? '-', item.porcentajemcupp ?? '-'
                    ]),
                    styles: { fontSize: 9, cellPadding: 3 },
                    headStyles: { fillColor: primaryColor },
                    alternateRowStyles: { fillColor: lightBg }
                });
                y = doc.lastAutoTable.finalY + 5;
                doc.autoTable({
                    startY: y,
                    margin: { left: margin, right: margin },
                    head: [['Totales', 'PVU Total', 'CVU Total', 'MCU Total', '%MCU']],
                    body: [[
                        'TOTALES',
                        contributionData.totalPvupp ?? '-',
                        contributionData.totalCvupp ?? '-',
                        contributionData.totalMcupp ?? '-',
                        contributionData.porcentajemcupp ?? '-'
                    ]],
                    styles: { fontSize: 9, fontStyle: 'bold', cellPadding: 3 },
                    headStyles: { fillColor: [52, 58, 64] }
                });
            } else {
                doc.setFontSize(10);
                doc.text('No hay datos de margen de contribución.', margin, y);
            }

            // --- 5. Competidores ---
            doc.addPage();
            y = margin;
            y = addSectionTitle('5. Clientes / Competidores', y);
            if (competitorData) {
                doc.autoTable({
                    startY: y,
                    margin: { left: margin, right: margin },
                    head: [['Campo', 'Valor']],
                    body: [
                        ['Competidores Directos', competitorData.competidorDirecto ?? '-'],
                        ['Competidores Indirectos', competitorData.competidorIndirecto ?? '-'],
                        ['Productos Sustitutos', competitorData.productoSustituto ?? '-'],
                    ],
                    styles: { fontSize: 9, cellPadding: 4 },
                    headStyles: { fillColor: primaryColor },
                    columnStyles: { 0: { fontStyle: 'bold', cellWidth: 60 } }
                });
            } else {
                doc.setFontSize(10);
                doc.text('No hay datos de competidores.', margin, y);
            }

            // --- 6. Financiación ---
            doc.addPage();
            y = margin;
            y = addSectionTitle('6. Financiación', y);
            const finArr = Array.isArray(financiacionData) ? financiacionData : [];
            if (finArr.length > 0) {
                doc.autoTable({
                    startY: y,
                    margin: { left: margin, right: margin },
                    head: [['Banco', 'Línea', 'Plazo', 'Interés Nominal', 'Interés EA', 'Monto', 'Cuota']],
                    body: finArr.map(f => [
                        f.banco || '-', f.linea || '-', f.plazo || '-',
                        f.interesNominal ?? '-', f.interesEfectivoAnual ?? '-',
                        f.montoFinanciar ?? '-', f.cuotaMensual ?? '-'
                    ]),
                    styles: { fontSize: 8, cellPadding: 3 },
                    headStyles: { fillColor: primaryColor },
                    alternateRowStyles: { fillColor: lightBg }
                });
            } else {
                doc.setFontSize(10);
                doc.text('No hay condiciones de financiación registradas.', margin, y);
            }

            // --- 7. Inversores ---
            doc.addPage();
            y = margin;
            y = addSectionTitle('7. Inversores Potenciales', y);
            const invArr = inversoresData?.investors || (Array.isArray(inversoresData) ? inversoresData : []);
            if (invArr.length > 0) {
                doc.autoTable({
                    startY: y,
                    margin: { left: margin, right: margin },
                    head: [['Nombre', 'Teléfono', 'Correo', 'Tipo de Inversor']],
                    body: invArr.map(inv => [
                        inv.nombre || '-', inv.telefono || '-', inv.correo || '-', inv.tipoInversor || '-'
                    ]),
                    styles: { fontSize: 9, cellPadding: 3 },
                    headStyles: { fillColor: primaryColor },
                    alternateRowStyles: { fillColor: lightBg }
                });
            } else {
                doc.setFontSize(10);
                doc.text('No hay inversores potenciales registrados.', margin, y);
            }

            // --- 8. Gastos ---
            doc.addPage();
            y = margin;
            y = addSectionTitle('8. Gastos No Recuperables', y);
            const gastosArr = gastosData?.investments || (Array.isArray(gastosData) ? gastosData : []);
            if (gastosArr.length > 0) {
                doc.autoTable({
                    startY: y,
                    margin: { left: margin, right: margin },
                    head: [['Rubro', 'Gasto Incurrido', 'Justificación']],
                    body: gastosArr.map(g => [
                        g.rubro || '-', g.gastoIncurrido ?? '-', g.justificacion || '-'
                    ]),
                    styles: { fontSize: 9, cellPadding: 3 },
                    headStyles: { fillColor: primaryColor },
                    alternateRowStyles: { fillColor: lightBg }
                });
                if (gastosData?.totalGastoIncurrido) {
                    const gy = doc.lastAutoTable.finalY + 5;
                    doc.setFont('helvetica', 'bold');
                    doc.setFontSize(10);
                    doc.text(`Total Gasto Incurrido: ${gastosData.totalGastoIncurrido}`, margin, gy);
                }
            } else {
                doc.setFontSize(10);
                doc.text('No hay gastos no recuperables registrados.', margin, y);
            }

            // --- 9. Plan de Negocio ---
            doc.addPage();
            y = margin;
            y = addSectionTitle('9. Plan de Negocio', y);
            if (planData) {
                doc.autoTable({
                    startY: y,
                    margin: { left: margin, right: margin },
                    head: [['Campo', 'Valor']],
                    body: [
                        ['Tipo de Empresa', planData.tipoEmpresa || '-'],
                        ['Modelo Aplicable', planData.modeloAplicable || '-'],
                        ['Costo del Plan', planData.costoPlanNegocio || '-'],
                        ['Fecha de Entrega', planData.fechaEntrega ? new Date(planData.fechaEntrega).toLocaleDateString('es-CO') : '-'],
                        ['Responsable', planData.responsable || '-'],
                        ['Apoyos Externos', planData.apoyosExternos || '-'],
                        ['Beneficios', planData.beneficios || '-'],
                    ],
                    styles: { fontSize: 9, cellPadding: 4 },
                    headStyles: { fillColor: primaryColor },
                    columnStyles: { 0: { fontStyle: 'bold', cellWidth: 50 } }
                });
            } else {
                doc.setFontSize(10);
                doc.text('No hay información del plan de negocio.', margin, y);
            }

            // --- 10. Estado de Resultados ---
            doc.addPage();
            y = margin;
            y = addSectionTitle('10. Estado de Resultados', y);
            if (estadoData) {
                doc.autoTable({
                    startY: y,
                    margin: { left: margin, right: margin },
                    head: [['Rubro', 'Escenario 1', 'Escenario 2', 'Escenario 3']],
                    body: [
                        ['Ventas (V)', estadoData.vescenario1 || '-', estadoData.vescenario2 || '-', estadoData.vescenario3 || '-'],
                        ['CMV', estadoData.cmvEscenario1 ?? '-', estadoData.cmvEscenario2 ?? '-', estadoData.cmvEscenario3 ?? '-'],
                        ['CMOD', estadoData.cmod || '-', estadoData.cmod || '-', estadoData.cmod || '-'],
                        ['CIF Semifijos', estadoData.ciff || '-', estadoData.ciff || '-', estadoData.ciff || '-'],
                        ['Utilidad Bruta', estadoData.ubEscenario1 ?? '-', estadoData.ubEscenario2 ?? '-', estadoData.ubEscenario3 ?? '-'],
                        ['Costo Fijo', estadoData.cf || '-', estadoData.cf || '-', estadoData.cf || '-'],
                        ['Utilidad Operacional', estadoData.uoEscenario1 ?? '-', estadoData.uoEscenario2 ?? '-', estadoData.uoEscenario3 ?? '-'],
                        ['Intereses', estadoData.iescenario1 || '-', estadoData.iescenario2 || '-', estadoData.iescenario3 || '-'],
                        ['UAI', estadoData.uaiEscenario1 ?? '-', estadoData.uaiEscenario2 ?? '-', estadoData.uaiEscenario3 ?? '-'],
                        ['Impuesto Renta', estadoData.vtEscenario1 ?? '-', estadoData.vtEscenario2 ?? '-', estadoData.vtEscenario3 ?? '-'],
                        ['Utilidad Neta', estadoData.uescenario1 || '-', estadoData.uescenario2 || '-', estadoData.uescenario3 || '-'],
                    ],
                    styles: { fontSize: 9, cellPadding: 3 },
                    headStyles: { fillColor: primaryColor },
                    alternateRowStyles: { fillColor: lightBg },
                    columnStyles: { 0: { fontStyle: 'bold' } }
                });
            } else {
                doc.setFontSize(10);
                doc.text('No hay datos de estado de resultados.', margin, y);
            }

            // --- 11. Sector Externo ---
            doc.addPage();
            y = margin;
            y = addSectionTitle('11. Sector Externo', y);
            y = addTextBlock('Descripción', ciemData.sectorExterno, y);

            // --- 12. Estructura de Producción ---
            y += 10;
            if (y > pageH - 60) { doc.addPage(); y = margin; }
            y = addSectionTitle('12. Estructura de Producción', y);
            y = addTextBlock('Descripción', ciemData.estructuraProduccion, y);

            // --- Add footers ---
            addFooter();

            // --- Output ---
            setProgress(100);
            setStatusMsg('¡Reporte generado!');
            const blob = doc.output('blob');
            const url = URL.createObjectURL(blob);
            setPdfUrl(url);

        } catch (err) {
            setError('Error al generar el reporte: ' + (err.message || err));
        } finally {
            setLoading(false);
        }
    };

    const handleDownload = () => {
        if (!pdfUrl) return;
        const a = document.createElement('a');
        a.href = pdfUrl;
        a.download = `reporte-ciem-${ciemId}.pdf`;
        a.click();
    };

    if (!ciemId) {
        return (
            <Container className="mt-4">
                <Alert variant="warning">
                    Por favor, seleccione un CIEM para generar el reporte PDF.
                </Alert>
            </Container>
        );
    }

    return (
        <Container fluid className="px-0">
            <Row>
                <Col xs={12} className="p-3">
                    <Card>
                        <Card.Header>
                            <div className="d-flex justify-content-between align-items-center">
                                <h3 className="mb-0">
                                    <FontAwesomeIcon icon={faFilePdf} className="me-2 text-danger" />
                                    Reporte PDF del CIEM
                                </h3>
                                <div className="d-flex gap-2">
                                    <Button
                                        variant="primary"
                                        onClick={generatePdf}
                                        disabled={loading}
                                    >
                                        {loading ? (
                                            <><FontAwesomeIcon icon={faSpinner} spin className="me-2" />Generando...</>
                                        ) : (
                                            <><FontAwesomeIcon icon={faSync} className="me-1" /> Generar Reporte</>
                                        )}
                                    </Button>
                                    {pdfUrl && (
                                        <Button variant="success" onClick={handleDownload}>
                                            <FontAwesomeIcon icon={faDownload} className="me-1" /> Descargar PDF
                                        </Button>
                                    )}
                                </div>
                            </div>
                        </Card.Header>
                        <Card.Body className="p-0">
                            {error && (
                                <div className="p-3">
                                    <Alert variant="danger" dismissible onClose={() => setError(null)}>
                                        {error}
                                    </Alert>
                                </div>
                            )}

                            {loading && (
                                <div className="text-center py-5 px-4">
                                    <Spinner animation="border" variant="primary" className="mb-3" />
                                    <ProgressBar now={progress} label={`${progress}%`} animated striped className="mb-2" style={{ height: '8px' }} />
                                    <p className="text-muted">{statusMsg}</p>
                                </div>
                            )}

                            {!loading && !pdfUrl && !error && (
                                <div className="text-center py-5 text-muted">
                                    <FontAwesomeIcon icon={faFilePdf} size="4x" className="mb-3 opacity-25" />
                                    <p className="mb-1">Haga clic en <strong>"Generar Reporte"</strong> para crear el PDF.</p>
                                    <p className="small">El reporte incluye portada, equipo, producto, contribución, financiación, inversores, gastos y estado de resultados.</p>
                                </div>
                            )}

                            {!loading && pdfUrl && (
                                <div style={{ width: '100%', height: '80vh' }}>
                                    <iframe
                                        src={pdfUrl}
                                        title="Reporte PDF CIEM"
                                        width="100%"
                                        height="100%"
                                        style={{ border: 'none', display: 'block' }}
                                    />
                                </div>
                            )}
                        </Card.Body>
                    </Card>
                </Col>
            </Row>
        </Container>
    );
};

export default ReportePdfPage;
